import { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { FileText, Plus, Loader2, Play, ExternalLink, CheckCircle2, XCircle, Clock, ChevronDown, Eye, Pencil, RotateCcw, Trash2 } from 'lucide-react';
import { api } from '../lib/api';
import { cn } from '../lib/cn';
import { PLATFORM_META, type Platform } from '../lib/platforms';
import { useAuth } from '../auth/AuthProvider';
import { detectTimezone, timezoneLabel, nowZoned, zonedDateToInput, zonedInputToIso } from '../lib/timezones';
import { AccountAvatar } from '../components/AccountAvatar';
import { ReusePostButton } from '../components/ReusePostButton';

/* ── Types ──────────────────────────────────────────────── */
type PostStatus =
  | 'draft' | 'scheduled' | 'publishing' | 'published'
  | 'partial_failed' | 'failed' | 'canceled' | 'pending_approval';

type Post = {
  id: string;
  caption: string;
  media: { url: string; type?: string; thumbnailUrl?: string | null }[];
  status: PostStatus;
  scheduledAt: string | null;
  publishedAt: string | null;
  createdAt: string;
  targets: {
    socialAccountId: string;
    status: string;
    kind: string;
    platform: string;
    platformUrl: string | null;
    username: string | null;
    displayName: string | null;
    profilePhotoUrl: string | null;
  }[];
};

const POST_STATUS: Record<PostStatus, { label: string; badge: string; dot: string }> = {
  published:      { label: 'Publicada',  badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  scheduled:      { label: 'Programada', badge: 'bg-amber-50 text-amber-700 border-amber-200',       dot: 'bg-amber-500' },
  publishing:     { label: 'Publicando', badge: 'bg-blue-50 text-blue-700 border-blue-200',          dot: 'bg-blue-500' },
  partial_failed: { label: 'Parcial',    badge: 'bg-orange-50 text-orange-700 border-orange-200',    dot: 'bg-orange-500' },
  failed:         { label: 'Falló',      badge: 'bg-red-50 text-red-700 border-red-200',             dot: 'bg-red-500' },
  draft:          { label: 'Borrador',   badge: 'bg-slate-100 text-slate-600 border-slate-200',      dot: 'bg-slate-400' },
  canceled:       { label: 'Cancelada',  badge: 'bg-slate-100 text-slate-500 border-slate-200',      dot: 'bg-slate-400' },
  pending_approval: { label: 'En aprobación', badge: 'bg-violet-50 text-violet-700 border-violet-200', dot: 'bg-violet-500' },
};

type Filter = 'scheduled' | 'publishing' | 'published' | 'error' | 'all';

const FILTER_IDS: Filter[] = ['scheduled', 'publishing', 'published', 'error', 'all'];
function isFilter(v: string | null): v is Filter {
  return v != null && (FILTER_IDS as string[]).includes(v);
}

/* ── Date helpers ───────────────────────────────────────── */
function fmtDateTime(iso: string, tz: string): string {
  const d = new Date(iso);
  const date = d.toLocaleDateString('es-ES', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: tz,
  });
  const time = d.toLocaleTimeString('es-ES', {
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: tz,
  });
  return `${date} · ${time}`;
}

function relativeFromNow(iso: string): string {
  const diff = new Date(iso).getTime() - Date.now();
  const abs = Math.abs(diff);
  const mins  = Math.round(abs / 60_000);
  const hours = Math.round(abs / 3_600_000);
  const days  = Math.round(abs / 86_400_000);
  let s: string;
  if (mins < 1)       s = 'un momento';
  else if (mins < 60) s = `${mins} min`;
  else if (hours < 24) s = `${hours} h`;
  else                s = `${days} d`;
  if (s === 'un momento') return 'ahora';
  return diff >= 0 ? `en ${s}` : `hace ${s}`;
}

/* ── Main page ──────────────────────────────────────────── */
export function Posts() {
  const { user } = useAuth();
  const tz = user?.timezone ?? detectTimezone();
  const [params, setParams] = useSearchParams();
  // Initial tab can be deep-linked via ?tab= (e.g. the success banner in the
  // composer sends users straight to "publishing" or "scheduled").
  const [filter, setFilterState] = useState<Filter>(() =>
    isFilter(params.get('tab')) ? (params.get('tab') as Filter) : 'scheduled',
  );
  const setFilter = (f: Filter) => {
    setFilterState(f);
    // Keep the URL in sync so the tab survives refresh / share, but don't
    // pile up history entries.
    setParams(f === 'scheduled' ? {} : { tab: f }, { replace: true });
  };

  const { data, isLoading } = useQuery({
    queryKey: ['posts'],
    queryFn: () => api<{ posts: Post[] }>('/api/posts'),
    // Poll while any post is in a non-terminal state (scheduled / publishing).
    // Once everything is published / failed / canceled / draft, polling stops.
    refetchInterval: (query) => {
      const posts = query.state.data?.posts ?? [];
      const hasInFlight = posts.some(
        (p) => p.status === 'scheduled' || p.status === 'publishing',
      );
      return hasInFlight ? 15_000 : false;
    },
    // Also refetch when the user re-focuses the tab (covers the case where
    // they left the tab open and come back after a publish window).
    refetchOnWindowFocus: true,
  });
  const posts = useMemo(() => data?.posts ?? [], [data]);

  const counts = useMemo(() => ({
    scheduled:  posts.filter((p) => p.status === 'scheduled' || p.status === 'draft').length,
    publishing: posts.filter((p) => p.status === 'publishing').length,
    published:  posts.filter((p) => p.status === 'published').length,
    error:      posts.filter((p) => p.status === 'failed' || p.status === 'partial_failed').length,
    all:        posts.length,
  }), [posts]);

  const visible = useMemo(() => {
    const ts = (p: Post) => new Date(p.scheduledAt ?? p.createdAt).getTime();
    if (filter === 'scheduled') {
      return posts
        .filter((p) => p.status === 'scheduled' || p.status === 'draft')
        .sort((a, b) => ts(a) - ts(b)); // soonest first
    }
    if (filter === 'publishing') {
      return posts.filter((p) => p.status === 'publishing').sort((a, b) => ts(b) - ts(a));
    }
    if (filter === 'published') {
      return posts.filter((p) => p.status === 'published').sort((a, b) => ts(b) - ts(a));
    }
    if (filter === 'error') {
      return posts
        .filter((p) => p.status === 'failed' || p.status === 'partial_failed')
        .sort((a, b) => ts(b) - ts(a));
    }
    return [...posts].sort((a, b) => ts(b) - ts(a));
  }, [posts, filter]);

  const TABS: { id: Filter; label: string; count: number }[] = [
    { id: 'scheduled',  label: 'Programadas', count: counts.scheduled },
    { id: 'publishing', label: 'Publicando',  count: counts.publishing },
    { id: 'published',  label: 'Publicadas',  count: counts.published },
    { id: 'error',      label: 'Con error',   count: counts.error },
    { id: 'all',        label: 'Todas',       count: counts.all },
  ];

  return (
    <div className="space-y-5">
      {/* header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><FileText size={22} className="text-brand-600" /> Publicaciones</h1>
          <p className="mt-1 text-sm text-slate-500">
            Todas tus publicaciones de un vistazo · horarios en {timezoneLabel(tz)}
          </p>
        </div>
        <Link to="/dashboard/posts/new" className="btn-primary gap-2">
          <Plus size={16} /> Crear publicación
        </Link>
      </div>

      {/* filter tabs */}
      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setFilter(t.id)}
            className={cn(
              'inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border transition',
              filter === t.id
                ? 'bg-brand-600 text-white border-brand-600'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50',
            )}
          >
            {t.label}
            <span className={cn(
              'text-xs rounded-full px-1.5 py-0.5 tabular-nums',
              filter === t.id ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500',
            )}>
              {t.count}
            </span>
          </button>
        ))}
      </div>

      {/* list */}
      {isLoading ? (
        <div className="flex items-center justify-center gap-2 text-slate-400 py-16">
          <Loader2 size={16} className="animate-spin" /> Cargando publicaciones…
        </div>
      ) : visible.length === 0 ? (
        <EmptyState filter={filter} />
      ) : (
        <div className="space-y-2">
          {visible.map((p) => (
            <PostRow key={p.id} post={p} tz={tz} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Post row ───────────────────────────────────────────── */
/* ── Republish a failed post (retry failed networks, optionally reschedule) ── */
function RepublishButton({ post, tz }: { post: Post; tz: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [when, setWhen] = useState(() => zonedDateToInput(nowZoned(tz)));
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const mut = useMutation({
    mutationFn: (scheduledAt: string | undefined) =>
      api(`/api/posts/${post.id}/republish`, {
        method: 'POST',
        body: JSON.stringify(scheduledAt ? { scheduledAt } : {}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['posts'] });
      setOpen(false);
    },
  });

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        title="Reprogramar y republicar"
        className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-700 bg-brand-50 hover:bg-brand-100 border border-brand-100 rounded-md px-2.5 py-1.5 transition"
      >
        <RotateCcw size={12} />
        Reprogramar
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-20 w-64 bg-white border border-slate-200 rounded-lg shadow-lg p-3 space-y-2 text-left">
          <p className="text-xs font-medium text-slate-700">Republicar las redes con error</p>
          <button
            type="button"
            disabled={mut.isPending}
            onClick={() => mut.mutate(undefined)}
            className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-md px-2.5 py-2 transition disabled:opacity-60"
          >
            {mut.isPending ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
            Republicar ahora
          </button>
          <div className="text-[11px] text-slate-400 text-center">o prográmalo para más tarde</div>
          <input
            type="datetime-local"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
            className="w-full text-xs border border-slate-200 rounded-md px-2 py-1.5"
          />
          <button
            type="button"
            disabled={mut.isPending || !when}
            onClick={() => mut.mutate(zonedInputToIso(when, tz))}
            className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-medium text-brand-700 border border-brand-200 hover:bg-brand-50 rounded-md px-2.5 py-1.5 transition disabled:opacity-60"
          >
            Programar
          </button>
          {mut.isError && <p className="text-[11px] text-red-600">No se pudo republicar. Inténtalo de nuevo.</p>}
        </div>
      )}
    </div>
  );
}

/* ── Delete a post (inline confirm) ─────────────────────── */
function DeletePostButton({ post }: { post: Post }) {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const mut = useMutation({
    mutationFn: () => api(`/api/posts/${post.id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['posts'] }),
  });

  if (confirming) {
    return (
      <span className="inline-flex items-center gap-1">
        <button
          type="button"
          disabled={mut.isPending}
          onClick={() => mut.mutate()}
          className="inline-flex items-center gap-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-md px-2 py-1.5 transition disabled:opacity-60"
        >
          {mut.isPending ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
          Confirmar
        </button>
        <button
          type="button"
          onClick={() => setConfirming(false)}
          className="text-xs text-slate-500 hover:text-slate-700 px-1.5 py-1.5"
        >
          Cancelar
        </button>
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setConfirming(true)}
      title="Borrar publicación"
      className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-red-600 hover:bg-red-50 border border-slate-200 hover:border-red-200 rounded-md px-2.5 py-1.5 transition"
    >
      <Trash2 size={12} />
      Borrar
    </button>
  );
}

function PostRow({ post, tz }: { post: Post; tz: string }) {
  const st = POST_STATUS[post.status] ?? POST_STATUS.draft;
  const thumb = post.media[0];
  const isVideo = thumb?.type === 'video';
  const when = post.status === 'published' && post.publishedAt
    ? post.publishedAt
    : post.scheduledAt;

  // Targets that actually got published with a live link
  const liveTargets = useMemo(
    () => post.targets.filter((t) => t.platformUrl),
    [post.targets],
  );
  const firstTarget = post.targets[0];

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 flex items-center gap-3.5">
      {/* thumbnail — img for photo, native <video> for video (renders 1st frame) */}
      <div className="relative h-14 w-14 rounded-lg bg-slate-100 shrink-0 overflow-hidden flex items-center justify-center">
        {thumb ? (
          isVideo ? (
            <>
              <video
                src={`${thumb.url}#t=0.5`}
                muted
                playsInline
                preload="metadata"
                className="h-full w-full object-cover"
              />
              <span className="absolute inset-0 flex items-center justify-center bg-black/30">
                <Play size={16} className="text-white drop-shadow" />
              </span>
            </>
          ) : (
            <img src={thumb.thumbnailUrl ?? thumb.url} alt="" loading="lazy" className="h-full w-full object-cover" />
          )
        ) : (
          <FileText size={18} className="text-slate-400" />
        )}
      </div>

      {/* main */}
      <div className="flex-1 min-w-0">
        <p className={cn('text-sm truncate', post.caption ? 'text-slate-800' : 'text-slate-400 italic')}>
          {post.caption || 'Sin texto'}
        </p>
        <div className="mt-1.5 flex items-center gap-2">
          {/* Stacked avatars (up to 4) */}
          {post.targets.length > 0 && (
            <div className="flex -space-x-1.5">
              {post.targets.slice(0, 4).map((t) => (
                <span key={t.socialAccountId} className="ring-2 ring-white rounded-full inline-flex">
                  <AccountAvatar
                    account={{
                      platform: t.platform as Platform,
                      username: t.username,
                      displayName: t.displayName,
                      profilePhotoUrl: t.profilePhotoUrl,
                    }}
                    size={20}
                  />
                </span>
              ))}
            </div>
          )}
          {/* Account name + extra count. The "+N" is a clickable chip that
              expands a popover listing every target with its per-platform
              status, so the user can see exactly where this post went. */}
          <span className="text-xs text-slate-600 truncate">
            {firstTarget
              ? firstTarget.displayName || firstTarget.username || PLATFORM_META[firstTarget.platform as Platform]?.label || ''
              : ''}
          </span>
          {post.targets.length > 1 && <TargetsPopover targets={post.targets} />}
        </div>
      </div>

      {/* status + date + view link */}
      <div className="flex flex-col items-end gap-1.5 shrink-0 text-right">
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {/* Scheduled posts get an "Editar" link so the user can fix the
              text, swap media, change networks or reschedule — anything
              still editable before the publishWorker picks it up. */}
          {(post.status === 'scheduled' || post.status === 'draft') && (
            <Link
              to={`/dashboard/posts/${post.id}/edit`}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-brand-700 hover:bg-brand-50 border border-slate-200 hover:border-brand-200 rounded-md px-2.5 py-1.5 transition"
              title="Editar publicación"
            >
              <Pencil size={12} />
              Editar
            </Link>
          )}
          {/* When the post landed on a single network, link straight to it.
              When it published to 2+ networks, collapse into a single
              "Ver publicación" button with a network picker so the row
              stays tidy regardless of how many platforms were involved. */}
          {liveTargets.length === 1 && (
            <ViewPostLink target={liveTargets[0]!} />
          )}
          {liveTargets.length > 1 && (
            <ViewPostPicker targets={liveTargets} />
          )}
          {/* Reuse an already-published post on the networks it didn't go to yet. */}
          {(post.status === 'published' || post.status === 'partial_failed') && (
            <ReusePostButton
              media={post.media}
              caption={post.caption}
              usedAccountIds={post.targets.map((t) => t.socialAccountId)}
            />
          )}
          {/* Failed posts: republish (retry the failed networks) or delete. */}
          {(post.status === 'failed' || post.status === 'partial_failed') && (
            <>
              <RepublishButton post={post} tz={tz} />
              <DeletePostButton post={post} />
            </>
          )}
          <span className={cn(
            'inline-flex items-center gap-1.5 text-xs font-medium rounded-full border px-2.5 py-1',
            st.badge,
          )}>
            <span className={cn('h-1.5 w-1.5 rounded-full', st.dot)} />
            {st.label}
          </span>
        </div>
        {when && (
          <div className="text-xs text-slate-500">
            <span className="hidden sm:inline">{fmtDateTime(when, tz)}</span>
            <span className="sm:hidden">{relativeFromNow(when)}</span>
            {post.status !== 'published' && (
              <span className="hidden sm:inline text-slate-400"> · {relativeFromNow(when)}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── "Ver publicación" link (single-network case) ───────── */
function ViewPostLink({ target }: { target: Post['targets'][number] }) {
  const meta = PLATFORM_META[target.platform as Platform];
  const Icon = meta?.icon;
  return (
    <a
      href={target.platformUrl!}
      target="_blank"
      rel="noopener noreferrer"
      title={`Abrir publicación en ${meta?.label ?? 'la red social'}`}
      className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-700 bg-brand-50 hover:bg-brand-100 border border-brand-100 rounded-md px-2.5 py-1.5 transition"
    >
      {Icon && meta && <Icon size={12} color={meta.color} />}
      Ver publicación
      <ExternalLink size={11} />
    </a>
  );
}

/* ── "Ver publicación" picker (multi-network case) ──────── */
function ViewPostPicker({ targets }: { targets: Post['targets'] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        className={cn(
          'inline-flex items-center gap-1.5 text-xs font-medium rounded-md px-2.5 py-1.5 border transition',
          open
            ? 'bg-brand-100 text-brand-700 border-brand-200'
            : 'bg-brand-50 text-brand-700 border-brand-100 hover:bg-brand-100',
        )}
        title={`Ver publicación (${targets.length} redes)`}
      >
        <Eye size={12} />
        Ver publicación
        <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-white/80 text-[10px] font-semibold text-brand-700 tabular-nums">
          {targets.length}
        </span>
        <ChevronDown
          size={11}
          className={cn('transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div className="absolute z-30 top-full mt-1.5 right-0 w-[280px] bg-white border border-slate-200 rounded-xl shadow-lg p-1.5">
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-2 py-1.5">
            Abrir en…
          </p>
          <ul className="space-y-0.5">
            {targets.map((t) => {
              const meta = PLATFORM_META[t.platform as Platform];
              const Icon = meta?.icon;
              const label = t.displayName ?? t.username ?? meta?.label ?? t.platform;
              return (
                <li key={t.socialAccountId}>
                  <a
                    href={t.platformUrl!}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-slate-50 transition group"
                  >
                    <AccountAvatar
                      account={{
                        platform: t.platform as Platform,
                        username: t.username,
                        displayName: t.displayName,
                        profilePhotoUrl: t.profilePhotoUrl,
                      }}
                      size={28}
                    />
                    <span className="flex-1 min-w-0">
                      <span className="block truncate text-sm text-slate-800 font-medium">{label}</span>
                      <span className="flex items-center gap-1 text-[11px] text-slate-500">
                        {Icon && meta && <Icon size={10} color={meta.color} />}
                        {meta?.label ?? t.platform}
                      </span>
                    </span>
                    <ExternalLink
                      size={13}
                      className="text-slate-300 group-hover:text-brand-600 transition"
                    />
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ── Targets popover (+N expand) ────────────────────────── */
type TargetItem = Post['targets'][number];

function TargetsPopover({ targets }: { targets: TargetItem[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const extra = targets.length - 1;

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        className={cn(
          'text-[11px] font-semibold rounded-full px-1.5 py-0.5 transition',
          open
            ? 'bg-brand-100 text-brand-700'
            : 'text-slate-500 bg-slate-100 hover:bg-slate-200 hover:text-slate-700',
        )}
        title="Ver todas las redes de esta publicación"
      >
        +{extra}
      </button>

      {open && (
        <div className="absolute z-30 top-full mt-1.5 left-0 w-[280px] bg-white border border-slate-200 rounded-xl shadow-lg p-1.5">
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-2 py-1.5">
            Publicada en {targets.length} {targets.length === 1 ? 'red' : 'redes'}
          </p>
          <ul className="space-y-0.5">
            {targets.map((t) => {
              const meta = PLATFORM_META[t.platform as Platform];
              const tgt = t.status as 'pending' | 'publishing' | 'published' | 'failed';
              const tooltip =
                tgt === 'published'
                  ? 'Publicado'
                  : tgt === 'failed'
                  ? 'Falló'
                  : tgt === 'publishing'
                  ? 'Publicando'
                  : 'Pendiente';
              const Status =
                tgt === 'published'
                  ? CheckCircle2
                  : tgt === 'failed'
                  ? XCircle
                  : Clock;
              const statusColor =
                tgt === 'published'
                  ? 'text-emerald-500'
                  : tgt === 'failed'
                  ? 'text-rose-500'
                  : 'text-slate-400';
              const label = t.displayName ?? t.username ?? meta?.label ?? t.platform;
              const Body = (
                <>
                  <AccountAvatar
                    account={{
                      platform: t.platform as Platform,
                      username: t.username,
                      displayName: t.displayName,
                      profilePhotoUrl: t.profilePhotoUrl,
                    }}
                    size={24}
                  />
                  <span className="flex-1 min-w-0">
                    <span className="block truncate text-sm text-slate-700">{label}</span>
                    <span className="block text-[10px] text-slate-400">{meta?.label ?? t.platform}</span>
                  </span>
                  <Status size={14} className={statusColor} aria-label={tooltip} />
                  {t.platformUrl && <ExternalLink size={12} className="text-slate-400" />}
                </>
              );
              return (
                <li key={t.socialAccountId}>
                  {t.platformUrl ? (
                    <a
                      href={t.platformUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 transition"
                    >
                      {Body}
                    </a>
                  ) : (
                    <span className="flex items-center gap-2 px-2 py-1.5 rounded-lg" title={tooltip}>
                      {Body}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ── Empty state ────────────────────────────────────────── */
function EmptyState({ filter }: { filter: Filter }) {
  const msg: Record<Filter, string> = {
    scheduled:  'No tienes publicaciones programadas.',
    publishing: 'No hay ninguna publicación en proceso ahora mismo.',
    published:  'Todavía no has publicado nada.',
    error:      'No hay publicaciones con errores. ¡Todo en orden!',
    all:        'Aún no has creado ninguna publicación.',
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm py-16 flex flex-col items-center gap-3">
      <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center">
        <FileText size={22} className="text-slate-400" strokeWidth={1.5} />
      </div>
      <p className="text-sm text-slate-500">{msg[filter]}</p>
      {filter !== 'error' && (
        <Link to="/dashboard/posts/new" className="btn-primary gap-2 mt-1">
          <Plus size={16} /> Crear publicación
        </Link>
      )}
    </div>
  );
}
